/*eslint max-lines-per-function: ["error", 460]*/
/*eslint complexity: ["error", 25]*/
import React, { useEffect, useState } from 'react';
import { Form, Card, Alert } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import { FormSelect } from '../../../app/components/FormSelect';
import {
  CreateUpdateExamWithTestsDto,
  CreateUpdateExamWithTestsDtoTypeEnum,
  CreateUpdateExamWithTestsDtoWhoExaminedEnum,
  CreateUpdateExaminedDto,
  CreateUpdateExternalReferralDto,
  GetBatteryDto,
  GetCompanyDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  ExamWhoExamined,
  ExamWhoExaminedText,
  getBirthDateFromPesel,
  getSexFromPesel,
  isPeselValid,
} from '../../../common/spdCore/validation/schemas';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  validate,
  validateWithoutPesel,
} from '../../../common/spdCore/contexts/ExternalReferralContext';
import momencik from '../../../common/momencik';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useNavigation } from '../../../common/navigation';
import BatterySelector from '../Battery/BatterySelector';
import HarmfulFactorMultiSelector from '../HarmfulFactor/HarmfulFactorMultiSelector';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import CompanyFromListSelector from '../Company/CompanyFromListSelector';

import StudentForm from './StudentForm';
import EmployeeForm from './EmployeeForm';
import ExaminedPersonalDataForm from './ExaminedPersonalDataForm';

interface Props {
  externalReferral: CreateUpdateExternalReferralDto;
  onSave?: (externalReferral: CreateUpdateExternalReferralDto) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ExternalReferral: React.FC<Props> = props => {
  const spdCore = useSpdCore();
  const nav = useNavigation();
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [kindOfExam, setKindOfExam] = useState<ExamWhoExamined>();
  const [batteries, setBatteries] = useState<GetBatteryDto[]>([]);
  const [externalReferral, setExternalReferral] =
    useState<CreateUpdateExternalReferralDto>(props.externalReferral);
  const [companies, setCompanies] = useState<GetCompanyDto[]>([]);
  const [company, setCompany] = useState<GetCompanyDto>();
  const [withoutPesel, setWithoutPesel] = useState<boolean>(false);

  useEffect(() => {
    setExternalReferral({
      ...props.externalReferral,
      examWithTests: {
        ...props.externalReferral.examWithTests,
        referralDate:
          props.externalReferral.examWithTests?.referralDate ?? new Date(),
      },
    });
  }, [props.externalReferral]);

  useEffect(() => {
    spdCore.companies
      .getMines()
      .then(loadedCompanies => {
        setCompanies(loadedCompanies);
        if (loadedCompanies.length === 1) {
          const only = loadedCompanies[0];
          setCompany(only);
          const batt = only?.companiesBatteries?.map(o => o.battery) || [];
          if (batt.length === 0) {
            setSaveErrors([
              'Brak baterii testów w firmie powiązanej z użytkownikiem. Zgłoś problem administratorowi.',
            ]);
            setShowSaveErrors(true);
          }
          setBatteries(batt);
          setExternalReferral(current => ({
            ...current,
            examWithTests: { ...current.examWithTests, companyId: only.id },
          }));
        } else if (loadedCompanies.length === 0) {
          setSaveErrors([
            'Brak firmy powiązanej z użytkownikiem. Zgłoś problem administratorowi.',
          ]);
          setShowSaveErrors(true);
        }
      })
      .catch(async error => {
        const err = await responseErrors(error);
        setSaveErrors([
          'Brak baterii testów. Zgłoś problem administratorowi. ' + err,
        ]);
        setShowSaveErrors(true);
      });
  }, [spdCore.companies]);

  const propertyChangeExam = (obj: Partial<CreateUpdateExamWithTestsDto>) => {
    setExternalReferral({
      ...externalReferral,
      ...{ examWithTests: { ...externalReferral.examWithTests, ...obj } },
    });
  };

  const propertyChangeExamined = (obj: Partial<CreateUpdateExaminedDto>) => {
    if (
      Object.prototype.hasOwnProperty.call(obj, 'pesel') &&
      isPeselValid(obj.pesel) &&
      obj.pesel
    ) {
      setExternalReferral({
        ...externalReferral,
        ...{
          examined: {
            ...externalReferral.examined,
            dateOfBirth: getBirthDateFromPesel(obj.pesel),
            sex: getSexFromPesel(obj.pesel),
            pesel: obj.pesel,
          },
        },
      });
    } else {
      setExternalReferral({
        ...externalReferral,
        ...{ examined: { ...externalReferral.examined, ...obj } },
      });
    }
  };

  const changeExternalReferral = (jobPositionId: string, fieldName: string) => {
    spdCore.jobPositions
      .get(jobPositionId)
      .then(jobPosition => {
        if (jobPosition) {
          let harmfulFactors = [];

          if (jobPosition.harmfulFactorsIds) {
            try {
              if (typeof jobPosition.harmfulFactorsIds === 'string') {
                harmfulFactors = JSON.parse(jobPosition.harmfulFactorsIds);
              } else {
                harmfulFactors = jobPosition.harmfulFactorsIds;
              }
            } catch (error) {
              console.error('Błąd przetwarzania harmfulFactorsIds:', error);
              harmfulFactors = [];
            }
          }

          if (fieldName === 'jobPosition') {
            setExternalReferral(currentState => ({
              ...currentState,
              examWithTests: {
                ...currentState.examWithTests,
                harmfulFactorsIds: harmfulFactors,
                employmentPosition: jobPosition.name,
              },
            }));
          } else {
            setExternalReferral(currentState => ({
              ...currentState,
              examWithTests: {
                ...currentState.examWithTests,
                harmfulFactorsIds: harmfulFactors,
                fieldOfStudy: jobPosition.name,
              },
            }));
          }
        }
      })
      .catch(error => {
        console.error('Błąd przetwarzania harmfulFactorsIds:', error);
      });
  };

  const handleCompanyChange = (selectedCompany: GetCompanyDto) => {
    setCompany(selectedCompany);
    const batt = selectedCompany?.companiesBatteries?.map(o => o.battery) || [];
    if (!batt || batt.length === 0) {
      setSaveErrors([
        'Brak baterii testów w wybranej firmie. Zgłoś problem administratorowi.',
      ]);
      setShowSaveErrors(true);
    } else {
      setShowSaveErrors(false);
    }
    setBatteries(batt);
    setExternalReferral(current => ({
      ...current,
      examWithTests: {
        ...current.examWithTests,
        companyId: selectedCompany.id,
        batteryId: undefined,
      },
    }));
  };

  const textOrEmpty = (text: string | null | undefined) => text || '';
  const putCompanyName = () => {
    if (company && (company.officialName || company.name)) {
      propertyChangeExam({
        employerName: company.officialName || company.name,
      });
    }
  };

  const putCompanyAddress = () => {
    if (company && company.address) {
      propertyChangeExam({ employerAddress: company.address });
    }
  };

  const putSchoolName = () => {
    if (company && (company.officialName || company.name)) {
      propertyChangeExam({ schoolName: company.officialName || company.name });
    }
  };

  const putSchoolAddress = () => {
    if (company && company.address) {
      propertyChangeExam({ schoolAddress: company.address });
    }
  };

  const onSave = async (
    externalReferral: CreateUpdateExternalReferralDto,
  ): Promise<boolean> => {
    try {
      const status = withoutPesel
        ? await validateWithoutPesel(externalReferral)
        : await validate(externalReferral);
      if (!kindOfExam) {
        setSaveErrors(['Proszę wskazać typ skierowania.']);
        setShowSaveErrors(true);
        setWaiting(false);
        return false;
      }
      if (!externalReferral?.examWithTests?.batteryId) {
        setSaveErrors([
          'Proszę wskazać, które badanie ma zostać wykonane badanemu.',
        ]);
        setShowSaveErrors(true);
        setWaiting(false);
        return false;
      }
      if (status.valid && props.onSave) {
        setWaiting(true);
        const saveStatus = await props.onSave(externalReferral);
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          setWaiting(false);
          return false;
        }
        setShowSaveErrors(false);
        setWaiting(false);
        return true;
      } else {
        setSaveErrors(status.errors);
        setShowSaveErrors(true);
        setWaiting(false);
        return false;
      }
    } catch {
      setSaveErrors([
        'Wystąpił błąd podczas dodawania skierowania zewnętrznego.',
      ]);
      setShowSaveErrors(true);
      setWaiting(false);
      return false;
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Subtitle className="mb-4">Dane ogólne</Card.Subtitle>
        <Form className="d-grid gap-3">
          <Alert
            variant="danger"
            className="d-flex justify-content-between align-items-center"
          >
            <div>
              W przypadku generowania skierowania na{' '}
              <strong>badanie sanitarne </strong>wybierz poniżej "Typ
              skierowania" - <strong>Do celów sanitarnych</strong> odpowiednio
              dla pracownika lub ucznia
            </div>
          </Alert>
          {companies.length > 1 && (
            <FormRow controlId="company" label="Firma/filia">
              <CompanyFromListSelector
                companies={companies}
                value={company?.id}
                onChange={option => {
                  const selected = companies.find(c => c.id === option.value);
                  if (selected) {
                    handleCompanyChange(selected);
                  }
                }}
              />
            </FormRow>
          )}
          <FormRow controlId="kind" label="Typ skierowania">
            <FormSelect
              options={[
                {
                  value: ExamWhoExamined.Employee,
                  label: ExamWhoExaminedText.Employee,
                },
                {
                  value: ExamWhoExamined.Sanitary,
                  label: ExamWhoExaminedText.Sanitary,
                },
                {
                  value: ExamWhoExamined.EmployeeAndSanitary,
                  label: ExamWhoExaminedText.EmployeeAndSanitary,
                },
                {
                  value: ExamWhoExamined.Student,
                  label: ExamWhoExaminedText.Student,
                },
                {
                  value: ExamWhoExamined.SanitaryStudent,
                  label: ExamWhoExaminedText.SanitaryStudent,
                },
                {
                  value: ExamWhoExamined.StudentAndSanitary,
                  label: ExamWhoExaminedText.StudentAndSanitary,
                },
              ]}
              onChange={value => {
                setKindOfExam(value as ExamWhoExamined);
                propertyChangeExam({
                  whoExamined:
                    value as CreateUpdateExamWithTestsDtoWhoExaminedEnum,
                });
              }}
            />
          </FormRow>
          <FormRow controlId="referralDate" label="Data skierowania">
            <Form.Control
              type="date"
              value={momencik(
                externalReferral?.examWithTests?.referralDate,
                'YYYY-MM-DD',
              )}
              onChange={e =>
                propertyChangeExam({
                  referralDate: new Date(e.target.value),
                })
              }
            />
          </FormRow>
          <FormRow controlId="battery" label="Badanie">
            <BatterySelector
              value={externalReferral?.examWithTests?.batteryId || undefined}
              onChange={e =>
                propertyChangeExam({
                  batteryId: e.value,
                })
              }
              batteries={batteries}
            />
          </FormRow>
          <FormRow controlId="type" label="Rodzaj skierowania">
            <FormSelect
              value={externalReferral?.examWithTests?.type}
              options={[
                {
                  value: CreateUpdateExamWithTestsDtoTypeEnum.Initial,
                  label: 'wstępne',
                },
                {
                  value: CreateUpdateExamWithTestsDtoTypeEnum.Periodic,
                  label: 'okresowe',
                },
                {
                  value: CreateUpdateExamWithTestsDtoTypeEnum.Control,
                  label: 'kontrolne',
                },
                {
                  value: CreateUpdateExamWithTestsDtoTypeEnum.PeriodicControl,
                  label: 'okresowo-kontrolne',
                },
              ]}
              onChange={value =>
                propertyChangeExam({
                  type: value as CreateUpdateExamWithTestsDtoTypeEnum,
                })
              }
            />
          </FormRow>
          {kindOfExam && (
            <>
              <Card.Subtitle className="mb-4 mt-4">
                {kindOfExam === ExamWhoExamined.Student ||
                kindOfExam === ExamWhoExamined.StudentAndSanitary ||
                kindOfExam === ExamWhoExamined.SanitaryStudent
                  ? 'Dane studenta/ucznia'
                  : 'Dane pracownika/kandydata do pracy'}
              </Card.Subtitle>

              <FormRow
                controlId="channel_public"
                label="Nieznany PESEL i nr dokumentu"
              >
                <Form.Check
                  type="checkbox"
                  checked={withoutPesel}
                  onChange={e => {
                    setWithoutPesel(e.target.checked);
                    propertyChangeExamined({
                      pesel: null,
                      iDCardType: null,
                      iDCardNumber: null,
                    });
                  }}
                />
              </FormRow>

              {/* Common personal data fields */}
              <ExaminedPersonalDataForm
                externalReferral={externalReferral}
                textOrEmpty={textOrEmpty}
                propertyChangeExamined={propertyChangeExamined}
                kindOfExam={kindOfExam}
                withoutPesel={withoutPesel}
              />
            </>
          )}
          {(kindOfExam === ExamWhoExamined.Student ||
            kindOfExam === ExamWhoExamined.StudentAndSanitary ||
            kindOfExam === ExamWhoExamined.SanitaryStudent) && (
            <StudentForm
              externalReferral={externalReferral}
              textOrEmpty={textOrEmpty}
              propertyChangeExam={propertyChangeExam}
              putSchoolName={putSchoolName}
              putSchoolAddress={putSchoolAddress}
              changeExternalReferral={changeExternalReferral}
              spdCore={spdCore}
            />
          )}
          {(kindOfExam === ExamWhoExamined.Employee ||
            kindOfExam === ExamWhoExamined.Sanitary ||
            kindOfExam === ExamWhoExamined.EmployeeAndSanitary) && (
            <EmployeeForm
              externalReferral={externalReferral}
              textOrEmpty={textOrEmpty}
              propertyChangeExam={propertyChangeExam}
              putCompanyName={putCompanyName}
              putCompanyAddress={putCompanyAddress}
              changeExternalReferral={changeExternalReferral}
              spdCore={spdCore}
            />
          )}
          <Card.Subtitle className="mb-4 mt-4">Inne</Card.Subtitle>
          <HarmfulFactorMultiSelector
            selectedHarmfulFactor={
              externalReferral?.examWithTests?.harmfulFactorsIds || undefined
            }
            onChange={options => {
              const harmfulFactorsIds = (options || [])?.map(o => o?.value);
              setExternalReferral(currentState => ({
                ...currentState,
                examWithTests: {
                  ...currentState.examWithTests,
                  harmfulFactorsIds: harmfulFactorsIds,
                },
              }));
            }}
          />
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
          <ConfirmationButton
            className="mx-1"
            disabled={waiting}
            confirmation="Skierowanie zostało wygenerowane. Czy przejść do wprowadzania kolejnych skierowań?"
            variant="outline-primary"
            onOK={nav.reload}
            onNoOK={nav.startPage}
            onValidation={() => onSave(externalReferral)}
          >
            Zapisz
          </ConfirmationButton>
        </Form>
      </Card.Body>
    </Card>
  );
};
